import {
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { Error, FindInPageOutlined, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"
import { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import { useCallback, useEffect, useMemo, useState } from "react"
import { ComponenteEstrutura, ListagemItem, EstruturaListagem, EstruturaListagemResponse, Projeto } from "src/components/projetos/types"
import { Layout } from "src/components/projetos/_layouts"
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList"
import InputQuantidade from "src/framework/components/forms/inputs/InputQuantidade"
import { apiNoLoading, IDataResponse } from "src/framework/providers/api"
import { setPersonalizadoTabDisabledStatus, setPersonalizadoTabDoneStatus } from "src/store/vendas/projetos/actions"
import theme from "src/theme/light"
import { TabName } from "../.."
import { TIPO_INVERSOR_ID } from "src/components/projetos/constants"
import { useObservable } from "src/framework/store/storeUtil"
import { fraseParceiro$ } from "src/store/global.store"
import { showPopupParceiroPowerAlert } from "src/components/projetos/utils/showPopupParceiroPowerAlert"
import { modalComponentesDetalhesData$ } from "src/store/vendas/projetos/store"
import EstruturasListMobile from "src/components/projetos/containers/Mobile/EstruturasListMobile"
import "./styles.css"
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";

interface EstruturasProps {
  onClickBack: () => void
  onClickNext: () => void
  formState: Projeto;
  onChangeFormState: (state: Projeto) => void
  tab: TabName
}

const useStyles = makeStyles({
  tableHeaderRow: {
    height: 60,
    backgroundColor: "#0000000D",
    cursor: 'pointer'
  },
  collapseContainer: {
    marginBottom: 20
  },
  warningText: {
    color: theme.palette.warning.main
  },
  confirmationButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.getContrastText(theme.palette.error.main),
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    }
  }
})

const Estruturas = ({ tab, onClickBack, onClickNext, formState, onChangeFormState }: EstruturasProps) => {
  const classes = useStyles()

  const [estruturasSelecionadas, setEstruturasSelecionadas] = useState<EstruturaListagem[]>(formState.estruturasSelecionadas || [])
  const [estruturasOpts, setEstruturasOpts] = useState<EstruturaListagem[]>([])

  const fraseParceiroPowerAlert = useObservable(fraseParceiro$);
  
  const [fraseParceiroExibidoList, setFraseParceiroExibidoList] = useState<number[]>([]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const labelPosicao = useCallback((posicao:string) => {
    switch (posicao) {
      case 'paisagem':
        return 'Paisagem';
      case 'retrato':
        return 'Retrato';
      case 'ambos':
        return 'Retrato / paisagem';
    }
  }, [])

  const selectedModulo = useMemo(()=>{
    const modulos = formState?.modulosList || []
    return modulos.find(item => item.selecionado)
  },[formState?.modulosList])

  useEffect(() => {

    // Executa na edição do projeto, pois, as estruturas vem no campo `estruturas_edicao` do formState
    if (!!estruturasOpts.length && !estruturasSelecionadas.length && !formState.estruturasSelecionadas?.length && formState.estruturas_edicao.length) {
      const newEstruturasSelecionadas = parserEstruturas(estruturasOpts, formState.estruturas_edicao || []);

      onChangeFormState({...formState, estruturas_edicao: [], estruturasSelecionadas: newEstruturasSelecionadas })
      setEstruturasSelecionadas(newEstruturasSelecionadas)
    }
  },[estruturasOpts, estruturasSelecionadas, formState, formState.estruturasSelecionadas, formState.estruturas_edicao, onChangeFormState])

  const parserEstruturas = (estruturas: EstruturaListagem[], estruturasSelecionadasEditadas: any[]) => {
    const newEstruturasSelecionadas = estruturas && estruturas.length ? estruturas.map(opt => {
      return {
        ...opt,
        opened: estruturasSelecionadasEditadas.findIndex(edicao => edicao.estruturas_grupo_id === opt.estruturas_grupo_id) > -1,
        items: opt.items.map(item => {
          const itemGrupo = estruturasSelecionadasEditadas.find(edicao => {
            return (item.componente_id === edicao.componente_id) && (edicao.estruturas_grupo_id === item.estruturas_grupo_id)
          })
          if (!itemGrupo) return item;       
          return { ...itemGrupo, quantidade: itemGrupo?.quantidade || item?.quantidade || 0, exibir_popup_parceiro: item?.exibir_popup_parceiro || false }
        })
      }
    }).filter(item => item.opened).filter(item => item.items.length > 0) :
    [];

    return newEstruturasSelecionadas;
  }

  const verificaEstruturasSelecionas = (estruturas: EstruturaListagem[], estruturasSelecionadasEditadas: any[]): boolean => {
    let atualizarEstruturaSelecionada = false;
    if(estruturasSelecionadasEditadas && estruturasSelecionadasEditadas.length){
      for(const estrutura of estruturasSelecionadasEditadas){
        atualizarEstruturaSelecionada = estruturas.findIndex(edicao => edicao.estruturas_grupo_id === estrutura.estruturas_grupo_id) < 0;
        if(atualizarEstruturaSelecionada){
          return atualizarEstruturaSelecionada;
        } 
      }
    }

    return atualizarEstruturaSelecionada;
  }
  
  useEffect(() => {
    if (formState?.alterou_inversor && estruturasOpts?.length) {
      const state = { ...formState }
      formState?.estruturasSelecionadas?.forEach((item) => {
        const estruturasIds = estruturasOpts?.map(estrutura => estrutura?.estrutura_id);
        const gruposIds = estruturasOpts?.map(estrutura => estrutura?.estruturas_grupo_id);
        if (item?.estrutura_id && (!estruturasIds?.includes(item?.estrutura_id) || !gruposIds?.includes(item?.estruturas_grupo_id))) {
          const newEstruturas = estruturasSelecionadas.filter((estrutura) => estrutura.estrutura_id !== item?.estrutura_id || estrutura.estruturas_grupo_id !== item?.estruturas_grupo_id)
          state.estruturasSelecionadas = newEstruturas
          onChangeFormState(state)     
          setEstruturasSelecionadas(newEstruturas)
        }
      })
    }
  }, [estruturasOpts, estruturasSelecionadas, formState, onChangeFormState])

  useEffect(()=>{
    if (tab !== 'estruturas') return;
    if (!selectedModulo) return;
    
    const currentRegioes = formState.regioes || []
    if (!currentRegioes.length) return;

    const potencia = Number(selectedModulo.quantidade || 0) * Number(selectedModulo.potencia || 0) / 1000

    const tipoInversor = TIPO_INVERSOR_ID[formState.tipo_inversor?.value || 0];
    let pathUrl = `/estoque/estruturas/modulos/${selectedModulo.componente_id}?potencia=${potencia}&regiao_id=${currentRegioes.map((item: any) => item.id).join(',')}&segmento=${formState.segmento?.includes("on") ? "on" : "off"}`;
    if (tipoInversor) pathUrl += `&inversor=${tipoInversor}`
    apiNoLoading.get({
      url: pathUrl
    }).then((res: IDataResponse<EstruturaListagemResponse[]>) => {
      const items = res.data || []
      const filterEstruturas = items?.filter(item => 
        [tipoInversor, "todos"]?.includes(item?.inversor) &&
        !!item.grupos.length && 
        item.grupos?.map(grupo => 
          grupo?.status && 
          [tipoInversor, "todos"]?.includes(grupo?.inversor) && 
          grupo.id
        )?.includes(item.estruturas_grupo_id)
      );
      const payload = filterEstruturas.map((item): EstruturaListagem => {
        const findGrupo = item.grupos?.find(g => g.id === item.estruturas_grupo_id) || item.grupos?.[0];
        const posicaoGrupo = labelPosicao(findGrupo?.posicao);
        return {
          estrutura_id: item.id,
          id: Number(item.estruturas_grupo_id ? `${item.id}.${item.estruturas_grupo_id}` : item.id),
          nome: posicaoGrupo ? `${item.nome} - ${posicaoGrupo}` : item?.nome,
          estruturas_grupo_id: item.estruturas_grupo_id,
          status: (item?.status && findGrupo?.status),
          posicao: findGrupo?.posicao,
          items: findGrupo ? findGrupo.componentes.map((componente: ComponenteEstrutura): ListagemItem => {
            return {
              altura: Number(componente.altura),
              categoria_componentes_id: Number(componente.categoria_componentes_id),
              componente_id: Number(componente.id),
              nome: componente.nome || '',
              descricao: componente.descricao || '',
              largura: Number(componente.largura),
              peso: Number(componente.peso),
              quantidade: 0,
              potencia: 0,
              valor: 0,
              estruturas_grupo_id: componente.estruturas_grupo_id,
              valor_unitario: 0,
              exibir_popup_parceiro: componente.exibir_popup_parceiro || false,
            }
          }) : []
        }
      })

      const _estruturasOpts = payload?.filter(estrutura => estrutura?.status);

      setEstruturasOpts(_estruturasOpts);

        // executa como é uma criação de projeto 
      if(estruturasSelecionadas.length && formState.estruturasSelecionadas?.length && !formState.estruturas_edicao.length && verificaEstruturasSelecionas(_estruturasOpts, formState.estruturasSelecionadas || [])) {
        const newEstruturasSelecionadas = parserEstruturas(_estruturasOpts, formState.estruturasSelecionadas || []);
  
        onChangeFormState({...formState, estruturasSelecionadas: newEstruturasSelecionadas });
        setEstruturasSelecionadas(newEstruturasSelecionadas);
      }
    })
  },[tab, selectedModulo, formState.regioes, formState.estruturas_edicao])

  const handleChangeOpenedStatus = useCallback((index: number) => {
    const newEstruturas = estruturasSelecionadas.map((item, i) => {
      if (i === index) return { ...item, opened: !item.opened }
      return item
    })
    setEstruturasSelecionadas(newEstruturas)
  }, [estruturasSelecionadas])

  const handleChangeItemQuantity = useCallback((estruturaId: number, itemId: number, value: any) => {
    const state = {...formState }
    const newEstruturasSelecionadas = estruturasSelecionadas.map(estrutura => {
      if(estrutura.id === estruturaId) {
        return {
          ...estrutura,
          items: estrutura.items.map(item => {
            if(item.componente_id === itemId) {
              if(item.exibir_popup_parceiro) {
                const idx = fraseParceiroExibidoList.findIndex(c => c === itemId);
                if(idx < 0 && value > 0) {
                  showPopupParceiroPowerAlert(fraseParceiroPowerAlert.data);
                  fraseParceiroExibidoList.push(itemId);
                } else {
                  if(value === 0) {
                    fraseParceiroExibidoList.splice(idx, 1);
                  }
                }
              }
              return {  ...item, quantidade: value }
            }
            return item;
          })
        }
      }
      return estrutura
    })

    state.estruturasSelecionadas = newEstruturasSelecionadas
    onChangeFormState(state)
    setEstruturasSelecionadas(newEstruturasSelecionadas)
  }, [formState, onChangeFormState, estruturasSelecionadas])

  const renderEstruturas = useCallback(()=>{

    if (isMobile) {
      return (
        <EstruturasListMobile
          estruturasList={estruturasSelecionadas || []}
          handleChangeItemQuantity={handleChangeItemQuantity}
          handleChangeOpenedStatus={handleChangeOpenedStatus}
        />
      )
    }

    return (
      <>
        {estruturasSelecionadas.map((estrutura, index) => (
          <Collapse key={estrutura.id} className={classes.collapseContainer} in={estrutura.opened} collapsedSize={60}>
            <TableContainer component={Paper}>
              <Table aria-label="listagem de modulos disponíveis">
                <TableHead>
                  <TableRow className={classes.tableHeaderRow} onClick={() => handleChangeOpenedStatus(index)}>
                    <TableCell align='left'><strong>{estrutura.nome}</strong></TableCell>
                    <TableCell align='center'><strong>Quantidade</strong></TableCell>
                    <TableCell align='center'><strong>Dados Técnicos</strong></TableCell>
                    <TableCell align='center'>
                      {estrutura.opened ? <KeyboardArrowUp /> : <KeyboardArrowDown /> }
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {estrutura.items.map(item => (
                    <TableRow key={item.componente_id}>
                      <TableCell style={{ maxWidth: '400px' }} align='right' component="th">
                        <Box display='flex'>
                          <Box textAlign={'left'}>
                            <Typography variant='body1' color='textPrimary'>
                              <strong>{item.nome}</strong>
                            </Typography>
                            <Typography variant='body2' color='textSecondary'>
                              <span className={classes.warningText}><strong>Essa opção inclui:</strong></span> {item.descricao}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align='center' component="th">
                        <Box width="180px" margin={"0 auto"}>
                          <InputQuantidade value={item.quantidade} onChange={(v: any) => handleChangeItemQuantity(estrutura.id, item.componente_id, v)} />
                        </Box>
                      </TableCell>
                      <TableCell align='center' component="th">
                        <Box display={"flex"} alignItems="center" justifyContent={"center"}>
                          <IconButton
                            onClick={() => item.componente_id && modalComponentesDetalhesData$.next(item.componente_id)}
                            size="large">
                              <FindInPageOutlined htmlColor="black" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell align='center' component="th" />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        ))}
      </>
    );
  },[classes, handleChangeItemQuantity, estruturasSelecionadas, handleChangeOpenedStatus])

  const handleOpenRemoveEstuturaModal = useCallback((onConfirm: () => void) => {
    confirmationDialog.setup({
      title: "Atenção!",
      subtitle: 'Exclusão de tipo de estrutura.',
      headerIcon: <Error color="error" />,
      content: (
        <>
          <span>Você tem certeza que deseja excluir esse tipo de estrutura?</span><br/>
          <span>Há produtos selecionados nesta seção que serão descartados. Por isso, reveja se a quantidade das estruturas mantidas será suficiente para a instalação do gerador.</span>
        </>
      ),
      noText: 'Cancelar',
      yesText: 'Excluir',
      submitButtonClassName: classes.confirmationButton,
      onClose: () => {
          confirmationDialog.close();
      },
      onConfirmation: () => {
        onConfirm()
      }
    })
    confirmationDialog.open()
  }, [classes.confirmationButton])

  const nextButtonIsDisabled = useMemo(()=>{
    return false
  },[])

  useEffect(()=>{
    setPersonalizadoTabDisabledStatus('acessorios', nextButtonIsDisabled)
    setPersonalizadoTabDoneStatus('estruturas', !nextButtonIsDisabled)
  },[nextButtonIsDisabled])

  return (
    <Layout formState={formState} nextButtonIsDisabled={nextButtonIsDisabled} onClickBack={onClickBack} onClickNext={onClickNext} title="Estrutura" subtitle="Escolha a estrutura que melhor atenderá este projeto." >
      <Box p={2} className="estruturas-personalizado">
        <Box mt={isMobile ? 0 : 3} mb={3}>
          <InputComboBoxList
            multiple
            id={`select-estruturas`}
            key={`select-estruturas`}
            label={"Tipo de estrutura"}
            textField="nome"
            valueField="id"
            options={estruturasOpts ? estruturasOpts.filter(item => !!item.items.length) : []}
            value={estruturasSelecionadas}
            autoComplete
            renderOptions={(option?: EstruturaListagem) => {
              return `${option?.nome}`
            }}
            renderOptionsLabel={(option?: EstruturaListagem) => {
              return `${option?.nome}`
            }}
            onChange={(value: EstruturaListagem[], item?: EstruturaListagem, reason?: AutocompleteChangeReason) => {
              const state = { ...formState }

              if (reason === 'clear') {
                const totalSelected = estruturasSelecionadas.reduce((acc, curr) => acc + curr.items.reduce((acc, curr) => acc + curr.quantidade, 0), 0)
                if (totalSelected === 0){
                  state.estruturasSelecionadas = []
                  onChangeFormState(state)  
                  setEstruturasSelecionadas([])
                  return;
                }                
                handleOpenRemoveEstuturaModal(() => {
                  state.estruturasSelecionadas = []
                  onChangeFormState(state)  
                  setEstruturasSelecionadas([])
                })
                return
              }

              if (reason === 'removeOption') {
                const currentItem = estruturasSelecionadas.find((estrutura) => estrutura.id === item?.id)
                const totalSelected = currentItem?.items?.reduce((acc, curr) => acc + curr.quantidade, 0) || 0
                const oldEstruturas = [...estruturasSelecionadas]
                const newEstruturas = oldEstruturas.filter((estrutura) => estrutura.id !== item?.id)
                if (totalSelected === 0) {
                  state.estruturasSelecionadas = newEstruturas
                  onChangeFormState(state)     
                  setEstruturasSelecionadas(newEstruturas)
                  return
                } 
                handleOpenRemoveEstuturaModal(() => {
                  state.estruturasSelecionadas = newEstruturas
                  onChangeFormState(state)     
                  setEstruturasSelecionadas(newEstruturas)
                })
                return;
              }

              if (value){
                const newEstruturas = value.map((item) => {
                  if (item) return { ...item, opened: item.opened !== undefined ? item.opened : true }
                  return item
                })
                setEstruturasSelecionadas(newEstruturas)
                return
              }

              state.estruturasSelecionadas = []
              onChangeFormState(state)   
              setEstruturasSelecionadas([])
            }}
            />
        </Box>

        {renderEstruturas()}
      </Box>
    </Layout>
  )
}

export { Estruturas }